import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, MessageCircle, Clock, CheckCircle, 
  AlertCircle, User, Phone, Filter, Send
} from 'lucide-react';
import { adminService } from '@/services/adminService';
import type { SupportTicket } from '@/types';

const OwnerSupport: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<SupportTicket[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [reply, setReply] = useState('');

  useEffect(() => {
    loadTickets();
  }, []);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredTickets(tickets);
    } else {
      setFilteredTickets(tickets.filter(t => t.status === statusFilter));
    }
  }, [statusFilter, tickets]);

  const loadTickets = async () => {
    try {
      const data = await adminService.getAllTickets();
      setTickets(data);
      setFilteredTickets(data);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedTicket || !reply) return;
    
    try {
      await adminService.resolveTicket(selectedTicket.id, reply);
      setReply('');
      setSelectedTicket(null);
      loadTickets();
    } catch (error) {
      console.error('Error resolving ticket:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      open: { bg: 'bg-red-100', text: 'text-red-700', label: 'مفتوحة' },
      in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'قيد المعالجة' },
      resolved: { bg: 'bg-green-100', text: 'text-green-700', label: 'محلولة' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'مغلقة' },
    };
    return badges[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
  };

  const getPriorityBadge = (priority: string) => {
    const badges: Record<string, { bg: string; text: string }> = {
      low: { bg: 'bg-gray-100', text: 'text-gray-700' },
      medium: { bg: 'bg-blue-100', text: 'text-blue-700' },
      high: { bg: 'bg-orange-100', text: 'text-orange-700' },
      urgent: { bg: 'bg-red-100', text: 'text-red-700' },
    };
    return badges[priority] || badges.low;
  };

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/owner/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">الدعم الفني</h1>
        </div>
      </header>

      <main className="p-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4">
            <p className="text-gray-500 text-sm">إجمالي التذاكر</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-gray-500 text-sm">مفتوحة</p>
            <p className="text-2xl font-bold text-red-600">{stats.open}</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-gray-500 text-sm">قيد المعالجة</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.inProgress}</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-gray-500 text-sm">محلولة</p>
            <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { key: 'all', label: 'الكل' },
            { key: 'open', label: 'مفتوحة' },
            { key: 'in_progress', label: 'قيد المعالجة' },
            { key: 'resolved', label: 'محلولة' },
            { key: 'closed', label: 'مغلقة' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-colors ${
                statusFilter === tab.key
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tickets List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">لا يوجد تذاكر</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTickets.map((ticket) => {
              const status = getStatusBadge(ticket.status);
              const priority = getPriorityBadge(ticket.priority);
              return (
                <div 
                  key={ticket.id} 
                  className="bg-white rounded-2xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-gray-800">{ticket.subject}</h3>
                      <p className="text-gray-500 text-sm">{ticket.description.slice(0, 100)}...</p>
                    </div>
                    <div className="flex gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${priority.bg} ${priority.text}`}>
                        {ticket.priority}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${status.bg} ${status.text}`}>
                        {status.label}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {ticket.userName}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {ticket.userPhone}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(ticket.createdAt).toLocaleDateString('ar-SD')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">تفاصيل التذكرة</h2>
                <button 
                  onClick={() => setSelectedTicket(null)}
                  className="p-2 hover:bg-gray-100 rounded-xl"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h3 className="font-bold text-lg mb-2">{selectedTicket.subject}</h3>
                <p className="text-gray-600">{selectedTicket.description}</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">المرسل</p>
                    <p className="font-medium">{selectedTicket.userName}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">الهاتف</p>
                    <p className="font-medium" dir="ltr">{selectedTicket.userPhone}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">النوع</p>
                    <p className="font-medium">{selectedTicket.category}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">التاريخ</p>
                    <p className="font-medium">
                      {new Date(selectedTicket.createdAt).toLocaleDateString('ar-SD')}
                    </p>
                  </div>
                </div>
              </div>

              {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
                <div>
                  <label className="block text-gray-700 font-medium mb-2">الرد</label>
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="اكتب ردك هنا..."
                    className="w-full border border-gray-200 rounded-xl p-4 h-32 focus:outline-none focus:border-primary-500 resize-none"
                  />
                  <button
                    onClick={handleResolve}
                    disabled={!reply}
                    className="w-full mt-4 bg-green-500 text-white py-3 rounded-xl font-medium hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    حل التذكرة
                  </button>
                </div>
              )}

              {selectedTicket.resolution && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-green-800 font-medium mb-1">الحل:</p>
                  <p className="text-green-700">{selectedTicket.resolution}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerSupport;
